import { ChangeGovernedTerm, SourcingEventStatus, type SourcingEventDto } from "../../../autogen/bff-api";
import { isNegotiationWithoutNotice } from "../../sourcing-events/buyer/wizard/utils/negotiationWithoutNotice";
import { isDpsScheme, isQualificationScheme, isTwoStageProcedure } from "../../sourcing-events/buyer/wizard/utils/procedure";
import { invitesItsSuppliers } from "./changeScope";

export type ChangeWindow = "open" | "reopenable" | "closed";

// Mirrors `SourcingEventV2.changeWindowClosesAt` on the BFF — keep the two in step.
const changeWindowClosesAt = (event: SourcingEventDto): string | null | undefined => {
  const publicContext = publicContextOf(event);
  if (isDpsScheme(publicContext?.procedureType)) return publicContext?.dpsScheme?.validTo;
  if (isQualificationScheme(publicContext?.procedureType)) return publicContext?.qualificationScheme?.validTo;
  if (event.changeGovernedTerm === ChangeGovernedTerm.RequestDeadline) {
    return publicContext?.qualificationRound?.requestDeadline ?? event.eventData.requestDeadline;
  }
  if (isTwoStageProcedure(publicContext?.procedureType)) return undefined;
  return event.milestoneDates?.bidSubmissionDeadline;
};

/** An FOA Part I procurement runs the private profile too, so "reopenable" covers both. */
export const changeWindowOf = (event: SourcingEventDto, now: number = Date.now()): ChangeWindow => {
  const closesAt = changeWindowClosesAt(event);
  if (closesAt == null) return "closed";
  if (new Date(closesAt).getTime() > now) return "open";
  return event.eventContext.type === "Private" ? "reopenable" : "closed";
};

// Mirrors `SourcingEventV2.isOpenForNewChange` on the BFF — keep the two in step.
export const isOpenForNewChange = (event: SourcingEventDto, now: number = Date.now()): boolean => {
  if (event.status === SourcingEventStatus.Published) return true;
  if (event.status !== SourcingEventStatus.Evaluation) return false;
  const deadline = event.milestoneDates?.bidSubmissionDeadline;
  return deadline != null && new Date(deadline).getTime() > now;
};

export interface DeadlineEditing {
  earliestSelectable: Date | undefined;
  canReopen: boolean;
  isClosed: boolean;
  warnsDeadlineIsNear: boolean;
}

const NEAR_DEADLINE_MS = 24 * 60 * 60 * 1000;

export const deadlineEditingOf = (event: SourcingEventDto, now: number = Date.now()): DeadlineEditing => {
  const closesAt = changeWindowClosesAt(event);
  const window = changeWindowOf(event, now);
  const sendsToDoffin =
    !invitesItsSuppliers(event) && !isNegotiationWithoutNotice(publicContextOf(event)?.procedureType);
  return {
    earliestSelectable:
      window === "reopenable" ? new Date(now) : closesAt != null ? new Date(closesAt) : undefined,
    canReopen: window === "reopenable",
    isClosed: window === "closed",
    warnsDeadlineIsNear:
      window === "open" &&
      sendsToDoffin &&
      closesAt != null &&
      new Date(closesAt).getTime() - now < NEAR_DEADLINE_MS,
  };
};

const publicContextOf = (event: SourcingEventDto) =>
  event.eventContext.type === "Public" ? event.eventContext : null;
